import type { CustomAssetAttributeDefinition } from '@/portalApi'
import { portalApi } from '@/util/apiClient'
import { byCaseInsensitiveFieldName } from '@/util/sorting'
import Toasts from '@/util/toasts'
import { defineStore } from 'pinia'
import { computed, getCurrentInstance, onMounted, ref, type Ref } from 'vue'

/**
 * Custom asset attribute definitions are needed by the admin settings page (manage
 * definitions), the asset detail view (render/edit each asset's values), and the asset
 * filter schema (build filterable fields dynamically) — so, unlike admin-settings-only
 * reference data (buildings, VRFs), this is cached in a shared store rather than fetched
 * directly in a single view.
 */
export const useCustomAssetAttributeDefinitionsStore = defineStore(
  'customAssetAttributeDefinitions',
  () => {
    const fetched = ref<Date | undefined>(undefined)
    const loading: Ref<boolean> = ref(false)

    const _definitionsRaw: Ref<CustomAssetAttributeDefinition[]> = ref([])
    const definitions = computed(() =>
      _definitionsRaw.value.sort(byCaseInsensitiveFieldName('name', true)),
    )

    const definitionsById = computed(() =>
      Object.fromEntries(definitions.value.map((d) => [d.id, d])),
    )

    // Tracks the in-flight fetch so concurrent callers (e.g. a router guard prefetching
    // ahead of the store's own onMounted hook) can await the same request instead of firing
    // a second one.
    let pendingFetch: Promise<void> | null = null

    const retrieveDefinitions = (): Promise<void> => {
      if (pendingFetch) {
        return pendingFetch
      }
      loading.value = true
      pendingFetch = portalApi
        .customAssetAttributesGet({ query: { order_by: 'name', limit: 1000 } })
        .then((response) => {
          fetched.value = new Date()
          _definitionsRaw.value = response.results || []
        })
        .catch((e) => {
          console.error(e)
          Toasts.Error(
            'Error fetching custom asset attribute definitions',
            'See console for details',
          )
        })
        .finally(() => {
          loading.value = false
          pendingFetch = null
        })
      return pendingFetch
    }

    // Used to prefetch ahead of schema-building code that runs synchronously during a
    // component's setup() — before this store's own onMounted-triggered fetch would resolve.
    // See the /assets route's beforeEnter guard in router/index.ts.
    const ensureLoaded = (): Promise<void> =>
      fetched.value ? Promise.resolve() : retrieveDefinitions()

    const saveDefinition = (
      value: CustomAssetAttributeDefinition,
    ): Promise<CustomAssetAttributeDefinition> => {
      const isNew = !value.id
      const performUpdate = isNew
        ? portalApi.customAssetAttributesPost({ body: value })
        : portalApi.customAssetAttributesPut({ body: value })
      return performUpdate.then((updated) => {
        _definitionsRaw.value = [
          ..._definitionsRaw.value.filter((d) => d.id !== updated.id),
          updated,
        ]
        return updated
      })
    }

    const deleteDefinition = (id: number): Promise<void> =>
      portalApi
        .customAssetAttributesDelete({ body: { id } })
        .then(() => {
          _definitionsRaw.value = _definitionsRaw.value.filter((d) => d.id !== id)
        })

    if (getCurrentInstance()) {
      onMounted(() => retrieveDefinitions())
    }

    return {
      // values
      definitions,
      definitionsById,
      fetched,
      loading,
      // functions
      deleteDefinition,
      ensureLoaded,
      retrieveDefinitions,
      saveDefinition,
    }
  },
)

export default useCustomAssetAttributeDefinitionsStore
